import { AxiosInstance } from 'axios'

// Constants
import { StateOfAutocomplete } from '@/constants/components/autocomplete'

/* Utils */
import { formatToAddressDelivery } from '@/api/modules/geo/geocode.utils'

/* Types */
import {
  Success,
  ServerError,
  HttpStatusCode,
  TooManyRequestError,
} from '@/api/results'
import { LatLng } from '@/api/modules/delivery/types.js'
import { Geoobject } from '@/types/api/api-v3/addresses'
import {
  SuggestedOfAddress,
  ConvertedDataByCoordinates,
  Bounds,
} from '@/api/modules/geo/geocode.types'
import { Mode } from '@/types/main'

export const createGeocodeService = ({
  axios,
  sentry,
  city,
  hostname,
}: {
  axios: AxiosInstance
  sentry: any
  city: string
  hostname: string
}) => {
  // Формируем новый url для v4
  const { API_SERVICE_ADDRESS, API_SERVICE_ADDRESS_KG, MODE } =
    useRuntimeConfig().public

  const API_URL = getApiUrl(
    API_SERVICE_ADDRESS,
    API_SERVICE_ADDRESS_KG,
    MODE as Mode,
    hostname,
  )

  return {
    // TODO: В будущем эти endpoint-ы нужно удалить,
    // пока что этот функционал нужен для фич-тагла
    async geocode(query: number[] | string) {
      if (typeof query === 'string') {
        return await this.getGeocodeOfCoordinates(query)
      } else {
        return await this.getGeocodeOfAddress(query)
      }
    },

    async getGeocodeOfCoordinates(query: string) {
      try {
        const { data } = await axios.get<Geoobject>(
          `${city}/geo/coordinates/`,
          {
            params: {
              address: query,
            },
          },
        )

        return new Success({
          address: formatToAddressDelivery(data.address),
          coordinates: data.coordinates,
        })
      } catch (error) {
        sentry.captureException(error)
        return new ServerError('Не удалось определить адрес')
      }
    },

    async getGeocodeOfAddress(query: number[] | string) {
      try {
        const { data } = await axios.get<Geoobject>(`${city}/geo/address/`, {
          params: {
            ll: query.toString(),
          },
        })

        return new Success({
          address: formatToAddressDelivery(data.address),
          coordinates: query as number[],
        })
      } catch (error) {
        sentry.captureException(error)
        return new ServerError('Не удалось определить адрес')
      }
    },

    /**
     * Поиск подсказок по query запросу с ограничением
     * @param query - строка поиска
     * @param bound - ограничение поиска
     * @param country - страна поиска
     * @param priorityRegion -приоритет поиска по региону
     * @param priorityCity - приоритет поиска по городу
     * @param locationCity - ограничение поиска по городу
     */
    async getSuggestedSearchesOfAddress(
      query: string,
      bound: Bounds,
      country: string,
      priorityRegion: string,
      priorityCity: string,
      locationCity?: string,
    ) {
      try {
        const { data } = await axios.post<SuggestedOfAddress[]>(
          `${API_URL}/api/v2/address/suggest/`,
          {
            country,
            search_term: query,
            to_bound: bound,
            boost_sorting_city: priorityCity,
            boost_sorting_region: priorityRegion,
            ...(locationCity && { location_city: locationCity }),
          },
        )

        if (!data.length)
          throw new ServerError(StateOfAutocomplete.NOT_DETECTED)

        return new Success(data)
      } catch (error) {
        sentry.captureException(error)

        switch (error.response?.status) {
          case HttpStatusCode.TOO_MANY_REQUESTS:
            return new TooManyRequestError(StateOfAutocomplete.TO_MANY_REQUEST)
          default:
            return new ServerError(StateOfAutocomplete.NOT_DETECTED)
        }
      }
    },
    /**
     * Поиск адреса по координатам
     * @param coords - координаты адреса
     * @param radius - радиус поиска
     */
    async getAddressByCoordinates(coords: LatLng, radius: number = 50) {
      const geoPoint = { latitude: coords[0], longitude: coords[1] }

      try {
        const { data } = await axios.post<SuggestedOfAddress>(
          `${API_URL}/api/v1/geo_locate/convert_geo_point_to_address/`,
          {
            geo_point: geoPoint,
            radius_meters: radius,
          },
        )

        const address = data ? { ...data, geo_point: geoPoint } : null

        return new Success(address)
      } catch (error) {
        sentry.captureException(error)

        switch (error.response?.status) {
          case HttpStatusCode.TOO_MANY_REQUESTS:
            return new TooManyRequestError(StateOfAutocomplete.TO_MANY_REQUEST)
          default:
            return new ServerError(StateOfAutocomplete.NOT_DETECTED)
        }
      }
    },
    /**
     * Поиск координат по адресу
     * @param address - полный литерал адреса
     */
    async getCoordinatesByAddress(address: string) {
      try {
        const { data } = await axios.post<ConvertedDataByCoordinates>(
          `${API_URL}/api/v1/geo_code/convert_address_to_geo_point/`,
          {
            address,
          },
        )

        // Случай, если координаты не найдены
        if (!data) throw new ServerError(StateOfAutocomplete.NOT_DETECTED)

        return new Success(data.geo_point)
      } catch (error) {
        sentry.captureException(error)

        switch (error.response?.status) {
          default:
            return new ServerError(StateOfAutocomplete.NOT_DETECTED)
        }
      }
    },
  }
}
